import appendStyles from './AddItems/AddStyles';
import appendCollapseButton, {
    collapseView,
    isSavedAsCollapsed,
} from './AddItems/AppendCollapseButton';
import appendLogo from './AddItems/AppendLogo';
import { appendMenuItems } from './AddItems/AppendMenuItems';
import {
    appendReorderButton,
    applySavedMenuItemOrder,
    initReorderMode,
} from './AddItems/ReorderMode';
import { renderBanner } from 'ff/common/components/FFMenuV2/Banner/RenderBanner';
import { appendPopup } from 'ff/common/components/FFMenuV2/AddItems/AppendPopup';
import { InitialMenuState } from 'shared/sharedTypes/ffmenu/InitialMenuState';
import { RenderMenuParams } from 'ff/common/components/FFMenuV2/RenderMenuParams';
import {
    LoadRemoteJeppNav,
    renderExportedJeppNav,
    shouldRenderExportedJeppNav,
} from './JeppNav/RenderJeppNav';

declare global {
    interface Window {
        renderMenu: (
            activeMenu: string,
            params: RenderMenuParams,
            loadRemoteJeppNav?: LoadRemoteJeppNav
        ) => void;
        notifications: Record<string, unknown>;
    }
}

const renderMenu = (
    activeMenu: string,
    params: RenderMenuParams,
    loadRemoteJeppNav?: LoadRemoteJeppNav
) => {
    console.log(
        `FFMenu build version: ${
            process.env.BUILD_VERSION || 'not supplied'
        }. Menu Config FFMenu build config: ${
            params.ffmenuBuildVersion || 'not supplied'
        }.`
    );
    const {
        currentUser,
        featureFlags,
        notifications,
        serverEnv,
        fspUser,
        isActive,
    } = params;
    //In case these werent set by the host app, set them there (bc this code expects them to be set)
    window.activeMenu = window.activeMenu || activeMenu;
    window.currentUser = window.currentUser || currentUser;
    window.featureFlags = window.featureFlags || featureFlags;
    window.notifications = window.notifications || notifications;
    window.serverEnv = window.serverEnv || serverEnv;
    window.fspUser = window.fspUser || fspUser;
    window.isActive = window.isActive || isActive;

    // The standalone bundle (used by other apps such as Traffic) always renders
    // the legacy menu. Only FFWeb's own bundle provides the exported Jepp nav.
    if (!process.env.STANDALONE_MENU && shouldRenderExportedJeppNav(params)) {
        // The exported Jepp nav renders its own managing-account banner, so the
        // legacy sidebar and banner are only shown as a fallback. Keep this
        // guarded so the common non-CA path continues to render synchronously.
        void renderExportedJeppNav(activeMenu, params, loadRemoteJeppNav)
            .then(rendered => {
                if (!rendered) {
                    renderLegacyMenu(params);
                }
            })
            .catch(error => {
                console.error('Unable to render the menu.', error);
                renderLegacyMenu(params);
            });
    } else {
        renderLegacyMenu(params);
    }
    // renderMilitaryAdModal(params);
};

export default renderMenu;
window.renderMenu = renderMenu;

function renderLegacyMenu(params: RenderMenuParams) {
    renderSideMenu(params);
    if (getOrCreateBannerRoot()) {
        renderBanner(params);
    }
}

function renderSideMenu(params: RenderMenuParams) {
    const root = getOrCreateSideMenuRoot();
    if (!root) {
        return;
    }

    let shadowRoot;
    if (root.shadowRoot) {
        //If the menu is rerendered and there is already a shadow root, clear it and use it to fill up again
        root.shadowRoot.innerHTML = '';
        shadowRoot = root.shadowRoot;
    } else {
        //Shadow root lets us add styles that are isolated from the rest of the dom. DOM styles won't
        // affect anything in the shadow root, and styles in the shadow root wont affect the rest of the DOM
        shadowRoot = root.attachShadow({ mode: 'open' });
    }

    const initializedState = params?.initialize?.initializedState;
    if (initializedState === InitialMenuState.EXPANDED) {
        //no op. Menu defaults to expanded.
    } else if (
        isSavedAsCollapsed() ||
        initializedState === InitialMenuState.COLLAPSED
    ) {
        //Do this first things before things get added to avoid flashing expanded vs collapse
        root.classList.add('x-collapse');
    } else if (initializedState === InitialMenuState.HIDDEN) {
        root.style.display = 'none';
    }

    appendStyles(shadowRoot);
    appendCollapseButton(shadowRoot, params);
    if (params.featureFlags?.['reorder_ffmenu'])
        appendReorderButton(shadowRoot);
    appendLogo(shadowRoot);
    appendMenuItems(params, shadowRoot);
    if (params.featureFlags?.['reorder_ffmenu']) {
        initReorderMode(shadowRoot, params);
        applySavedMenuItemOrder(shadowRoot, params);
    }

    if (
        isSavedAsCollapsed() ||
        params?.initialize?.initializedState === InitialMenuState.COLLAPSED
    ) {
        collapseView(shadowRoot, true);
    }

    initSubSectionStyles(shadowRoot);

    appendPopup(shadowRoot);
}

function getOrCreateSideMenuRoot(): HTMLElement | null {
    const existingRoot = document.getElementById('ffweb-side-menu');
    if (existingRoot) {
        return existingRoot;
    }

    const jeppNavRoot = document.getElementById('ffweb-jepp-nav');
    if (!jeppNavRoot) {
        return null;
    }

    const root = document.createElement('div');
    root.id = 'ffweb-side-menu';
    jeppNavRoot.before(root);

    //Defensive cleanup: the legacy sidebar is being rendered instead of the
    //exported Jepp nav, so make sure the wrapper isn't left in the
    //column-layout state the exported nav uses.
    jeppNavRoot
        .closest<HTMLElement>('.main-wrapper')
        ?.classList.remove('jepp-nav-pending', 'jepp-nav-loaded');

    return root;
}

function getOrCreateBannerRoot(): HTMLElement | null {
    const existingRoot = document.getElementById(
        'ffweb-managing-account-banner'
    );
    if (existingRoot) {
        return existingRoot;
    }

    //The server doesn't render this host when the exported Jepp nav is
    //expected to show its own banner. If we're here, that nav failed to
    //load and we're falling back to the legacy menu, so recreate it.
    const mainContentsWrapper = document.getElementById(
        'main-contents-wrapper'
    );
    if (!mainContentsWrapper) {
        return null;
    }

    const root = document.createElement('div');
    root.id = 'ffweb-managing-account-banner';
    root.style.flex = '0 0 auto';
    mainContentsWrapper.prepend(root);
    return root;
}

function initSubSectionStyles(root: ShadowRoot) {
    const subSections = root.querySelectorAll<HTMLElement>(
        '.sub-section-contents'
    );
    subSections.forEach(el => {
        let initiallyClosed = false;
        if (el.classList.contains('sub-section-collapse')) {
            el.classList.remove('sub-section-collapse');
            initiallyClosed = true;
        }
        const x = getComputedStyle(el);
        //get the height so we can explicitly set it, so height animation works
        el.style.height = x.getPropertyValue('height');
        if (initiallyClosed) {
            el.classList.add('sub-section-collapse');
        }
    });
}
